import ProgressIndicator from '@/components/progressIndicator';
import ErrorMessage from '@/components/errorMessage';
import { useApiContext } from '@/contexts/apiContext';
import {
  Box,
  Button,
  Stack,
  styled,
  Typography,
  useTheme,
  alpha,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import DataSourceInstanceIcon from './dataSourceInstanceIcon';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { useUserContext } from '@/contexts/userContext';

const DataSourceInstances = () => {
  const apiContext = useApiContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { hasPermission } = useUserContext();

  const [containerRef, containerSize] = useElementSize();
  const [topPanelRef, topPanelSize] = useElementSize();

  const [viewFilter, setViewFilter] = useState<'owned' | 'shared'>('owned');
  const [dataSources, setDataSources] = useState<any>([]);

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<any>();
  const [sortBy, setSortBy] = useState<string>('lastUpdatedDate');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  const sortedDataSources = [...dataSources].sort((a, b) => {
    let aVal: any = '';
    let bVal: any = '';

    if (sortBy === 'name') {
      aVal = a.name?.toLowerCase() || '';
      bVal = b.name?.toLowerCase() || '';
    } else if (sortBy === 'lastUpdatedDate') {
      aVal = a.lastUpdatedDate || a.createdDate || '';
      bVal = b.lastUpdatedDate || b.createdDate || '';
    } else if (sortBy === 'createdDate') {
      aVal = a.createdDate || '';
      bVal = b.createdDate || '';
    }

    if (aVal < bVal) return sortOrder === 'ASC' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'ASC' ? 1 : -1;
    return 0;
  });

  const getUserDataSources = async () => {
    try {
      setLoading(true);
      const fetchedDataSources = await apiContext.getDataSourceInstances(
        viewFilter === 'owned',
        viewFilter === 'shared',
      );
      setDataSources(fetchedDataSources);
    } catch (error: any) {
      setApiError(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    getUserDataSources();
  }, [viewFilter]);

  const openDataSourceInstance = (dataSource: any) => {
    navigate(`/dataSourceInstance/${dataSource.dataSourceDef.id}/${dataSource.id}`, {
      state: { from: location.pathname + location.search },
    });
  };

  const createDataSourceInstance = () => {
    navigate('/createDataSourceInstance');
  };

  return (
    <Box
      className="glass-card-light"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '20px',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        bgcolor: 'rgba(255, 255, 255, 0.4)',
      }}
      ref={containerRef}
    >
      <Box
        sx={{
          px: 2,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          gap: 1.5,
        }}
        ref={topPanelRef}
      >
        <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            Data Sources
          </Typography>
          <ToggleButtonGroup
            size="small"
            value={viewFilter}
            exclusive
            onChange={(e, newView) => newView && setViewFilter(newView)}
            sx={{ height: 28 }}
          >
            <ToggleButton value="owned" sx={{ textTransform: 'none', px: 2, fontSize: '11px', fontWeight: 600 }}>
              My Datasources
            </ToggleButton>
            <ToggleButton value="shared" sx={{ textTransform: 'none', px: 2, fontSize: '11px', fontWeight: 600 }}>
              Shared with Me
            </ToggleButton>
          </ToggleButtonGroup>
          {dataSources.length > 0 && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                px: 1,
                py: 0.25,
                borderRadius: '6px',
              }}
            >
              {dataSources.length}
            </Typography>
          )}
        </Stack>
        {viewFilter === 'owned' && hasPermission('pipeline:dataSources:create') && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={createDataSourceInstance}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 1.5,
              py: 0.25,
              fontSize: '12px',
              color: 'text.primary',
              boxShadow: 'none',
            }}
          >
            {t('dataSource.dataSourceInstances.actions.create')}
          </Button>
        )}
      </Box>

      {/* Sort Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.paper, 0.1),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
          <SortIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 750,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              fontSize: '8px',
            }}
          >
            Sort By
          </Typography>

          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as string)}
            variant="standard"
            disableUnderline
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              color: theme.palette.secondary.main,
              cursor: 'pointer',
              '& .MuiSelect-select': {
                py: 0.2,
                pr: '20px !important',
              },
            }}
          >
            <MenuItem value="lastUpdatedDate" sx={{ fontSize: '11px', fontWeight: 700 }}>
              Last Updated
            </MenuItem>
            <MenuItem value="createdDate" sx={{ fontSize: '11px', fontWeight: 700 }}>
              Created Date
            </MenuItem>
            <MenuItem value="name" sx={{ fontSize: '11px', fontWeight: 700 }}>
              Name
            </MenuItem>
          </Select>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={sortOrder === 'ASC' ? 'Ascending' : 'Descending'}>
            <IconButton
              size="small"
              onClick={() => setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'))}
              sx={{
                width: 20,
                height: 20,
                borderRadius: '4px',
                bgcolor: alpha(theme.palette.secondary.main, 0.06),
                color: theme.palette.secondary.main,
                '&:hover': {
                  bgcolor: alpha(theme.palette.secondary.main, 0.12),
                },
              }}
            >
              {sortOrder === 'ASC' ? (
                <ArrowUpwardIcon sx={{ fontSize: 11 }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: 11 }} />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Refresh">
            <IconButton
              size="small"
              onClick={getUserDataSources}
              sx={{
                width: 20,
                height: 20,
                borderRadius: '4px',
                bgcolor: alpha(theme.palette.secondary.main, 0.06),
                color: theme.palette.secondary.main,
                '&:hover': {
                  bgcolor: alpha(theme.palette.secondary.main, 0.12),
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 11 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {apiError && (
        <Box sx={{ p: 2 }}>
          <ErrorMessage error={apiError} />
        </Box>
      )}

      <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
        {loading ? (
          <ProgressIndicator />
        ) : (
          <Stack spacing={1.5} rowGap={0.75} justifyContent={'flex-start'} direction={'row'} useFlexGap flexWrap="wrap">
            {dataSources?.length === 0 && (
              <Box sx={{ width: '100%', py: 4, textAlign: 'center', opacity: 0.6 }}>
                <Typography variant="body2">
                  {viewFilter === 'owned' ? 'No datasources created yet.' : 'No datasources have been shared with you.'}
                </Typography>
              </Box>
            )}
            {sortedDataSources.map((dataSourceInstance: any) => (
              <DataSourceInstanceIcon
                key={dataSourceInstance.id}
                dataSourceInstance={dataSourceInstance}
                openDataSourceInstance={openDataSourceInstance}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default DataSourceInstances;
